import {
  Dialog,
  DialogContent,
  TextField,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  InputAdornment,
  Typography,
  Box,
  Chip,
  IconButton,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import DashboardIcon from '@mui/icons-material/Dashboard';
import MessageIcon from '@mui/icons-material/Message';
import AssessmentIcon from '@mui/icons-material/Assessment';
import MapIcon from '@mui/icons-material/Map';
import ArticleIcon from '@mui/icons-material/Article';
import SettingsIcon from '@mui/icons-material/Settings';
import HubIcon from '@mui/icons-material/Hub';
import AddIcon from '@mui/icons-material/Add';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HistoryIcon from '@mui/icons-material/History';
import StarIcon from '@mui/icons-material/Star';
import type { CommandItem } from '@/hooks/useCommandBar.js';

interface CommandBarProps {
  open: boolean;
  onClose: () => void;
  query: string;
  onQueryChange: (value: string) => void;
  items: CommandItem[];
  selectedIndex: number;
  onSelect: (index: number) => void;
  onExecute: (item: CommandItem) => void;
  inputRef: React.RefObject<HTMLInputElement | null>;
}

const iconMap: Record<string, React.ReactNode> = {
  dashboard: <DashboardIcon fontSize="small" />,
  message: <MessageIcon fontSize="small" />,
  scenario: <AssessmentIcon fontSize="small" />,
  analyzer: <AssessmentIcon fontSize="small" />,
  gis: <MapIcon fontSize="small" />,
  log: <ArticleIcon fontSize="small" />,
  report: <ArticleIcon fontSize="small" />,
  integration: <HubIcon fontSize="small" />,
  settings: <SettingsIcon fontSize="small" />,
  add: <AddIcon fontSize="small" />,
  play: <PlayArrowIcon fontSize="small" />,
  check: <CheckCircleIcon fontSize="small" />,
  history: <HistoryIcon fontSize="small" />,
  star: <StarIcon fontSize="small" />,
};

export const CommandBar = ({
  open,
  onClose,
  query,
  onQueryChange,
  items,
  selectedIndex,
  onSelect,
  onExecute,
  inputRef,
}: CommandBarProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      slotProps={{
        paper: {
          sx: { borderRadius: 2, overflow: 'hidden' },
        },
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        <TextField
          inputRef={inputRef}
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          placeholder="메뉴, 자산, 기능 검색..."
          fullWidth
          variant="standard"
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={onClose} edge="end">
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
              sx: { px: 2, py: 1.5 },
            },
          }}
        />
        <Box sx={{ maxHeight: isMobile ? '60vh' : '50vh', overflow: 'auto' }}>
          {items.length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography color="text.secondary">검색 결과가 없습니다.</Typography>
            </Box>
          ) : (
            <List dense>
              {items.map((item, index) => (
                <ListItem key={item.id} disablePadding>
                  <ListItemButton
                    selected={index === selectedIndex}
                    onMouseEnter={() => onSelect(index)}
                    onClick={() => onExecute(item)}
                    sx={{ px: 2, py: 1 }}
                  >
                    <Box sx={{ mr: 1.5, display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
                      {iconMap[item.icon ?? ''] ?? <SearchIcon fontSize="small" />}
                    </Box>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {item.label}
                          </Typography>
                          <Chip label={item.type} size="small" variant="outlined" sx={{ height: 18, fontSize: 10 }} />
                        </Box>
                      }
                      secondary={item.description}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          )}
        </Box>
        <Box
          sx={{
            px: 2,
            py: 1,
            borderTop: 1,
            borderColor: 'divider',
            display: 'flex',
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          <Typography variant="caption" color="text.secondary">
            ↑↓ 선택
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Enter 실행
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Esc 닫기
          </Typography>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default CommandBar;
